import * as React from 'react';
import {observer} from 'mobx-react';
import {style} from 'typestyle';
import {important, percent, px, quote, url} from 'csx';
import {colorBackground, colorBlack, colorBrand, colorSubtle, colorWhite} from '../theme/color';
import {SVGIcon} from './svg-icon';
import {shadowNotification} from '../theme/shadow';
import {centerCenter, centerJustified, flex1, horizontal, inlineRoot, vertical} from 'csstips';
import {ButtonCircle} from './button-circle';
import {mediaMobileOnly, mediaTablet} from '../theme/media';

export interface NotificationModel {
  image: string;
  title: string;
  message: string;
}

export interface NotificationProps {
  model?: NotificationModel;
  onRequestClose(): void;
}

const styles = {
  container: style(
    {
      ...horizontal,
      ...inlineRoot,
      position: 'relative',
      height: px(76),
      borderRadius: px(4),
      background: colorWhite.toString(),
      boxShadow: shadowNotification,
      $nest: {
        '&::after': {
          position: 'absolute',
          top: px(-9),
          right: px(28),
          content: quote(''),
          height: 0,
          width: 0,
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderBottom: `10px solid ${colorWhite.toString()}`,
        },
        '&::before': {
          position: 'absolute',
          top: px(-10),
          right: px(28),
          content: quote(''),
          height: 0,
          width: 0,
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderBottom: `10px solid ${colorBlack.fade(0.05).toString()}`,
        },
        '&> div:last-child': {
          ...centerCenter,
          width: px(76)
        }
      }
    },
    mediaMobileOnly({
      width: percent(100),
      borderRadius: 0,
    }),
    mediaTablet({
      maxWidth: px(420),
      minWidth: px(320)
    })
  ),
  image: style(
    {
      width: px(76),
      height: px(76),
      backgroundPosition: 'center center',
      backgroundSize: 'cover',
      borderRadius: '4px 0 0 4px',
    },
    mediaMobileOnly({
      borderRadius: 0
    })
  ),
  message: style({
    ...flex1,
    ...vertical,
    ...centerJustified,
    padding: '14px 0 14px 14px',
    fontWeight: 600,
    $nest: {
      '&> div': {
        lineHeight: 1.23,
      },
      '&> div:last-child': {
        fontSize: px(13),
        color: colorSubtle.toString(),
        fontWeight: 'normal'
      }
    }
  }),
  close: style({
    backgroundColor: important(colorBackground.toString()),
    cursor: 'pointer',
  })
};

const DEFAULT_MODEL = {
  image: 'http://cdn.pitchfork.com/albums/21726/8f09545c.jpg',
  title: 'A notification',
  message: 'This is the message of the notification, which looks best if two lines long'
};

export const Notification = observer<NotificationProps>(
  ({ model = DEFAULT_MODEL, onRequestClose }) => (
    <div className={styles.container}>
      {model.image && (
        <div className={styles.image} style={{ backgroundImage: url(model.image) }} />
      )}
      <div className={styles.message}>
        <div>{model.title}</div>
        <div>{model.message}</div>
      </div>
      <div>
        <ButtonCircle size={px(28)} className={styles.close} onClick={onRequestClose}>
          <SVGIcon.Cross size={12} color={colorBrand.toString()} />
        </ButtonCircle>
      </div>
    </div>
  )
);